import { ReactNode, forwardRef, useCallback } from 'react';
import { css, Theme } from '@emotion/react';
import { Switch } from 'client/components/Switch';
import { Button, ButtonProps } from './Button';

export interface ToggleButtonProps extends Omit<ButtonProps, 'onChange'> {
  checked?: boolean;
  disabled?: boolean;
  children: ReactNode;
  onChange?: (checked: boolean) => void;
}

export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  ({ checked = false, disabled = false, children, onChange, ...other }, ref) => {
    const handleToggle = useCallback(() => onChange && onChange(!checked), [checked, onChange]);

    return (
      <Button
        ref={ref}
        role="checkbox"
        aria-checked={checked}
        icon={<Switch checked={checked} tabIndex={-1} disabled={disabled} component="div" />}
        primary={false}
        disabled={disabled}
        css={[buttonStyle, checked && buttonCheckedStyle]}
        onClick={handleToggle}
        {...other}
      >
        {children}
      </Button>
    );
  }
);

const buttonStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  box-shadow: none;

  &:hover {
    box-shadow: none;
  }

  .icon {
    margin-right: 14px;
    opacity: 1;
  }

  .switch > label {
    display: none;
  }

  .switch > div {
    background-color: ${theme.color.background};
  }
`;

const buttonCheckedStyle = (theme: Theme) => css`
  .switch > div {
    background-color: ${theme.color.primary[100]};
  }
`;
